import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import type { Almacen } from '../../stores/almacenes/almacen';
import type { ActualizarAlmacen, RegistrarAlmacen } from '../../stores/almacenes/almacenes.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import AppTextField from '../ui/AppTextField';
import EditForm from '../ui/EditForm';
import LabeledSwitch from '../ui/LabeledSwitch';

const validationSchema = yup.object({
  id: yup.string().required('El código es requerido'),
  nombre: yup.string().required('El nombre es requerido'),
  permiteMovsSinExistencia: yup.boolean()
});

type AlmacenFormProps = {
  almacen?: Almacen;
  onSave:
    | ((values: ActualizarAlmacen) => Promise<void>)
    | ((values: RegistrarAlmacen) => Promise<boolean>);
} & CommonEntityFormProps;

const AlmacenForm = ({ almacen, esAgregar, onSave, onDelete, onActivate }: AlmacenFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || almacen?.activo === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore((state) => state);
  const initialValues = {
    id: almacen?.id ?? '',
    nombre: almacen?.nombre ?? '',
    permiteMovsSinExistencia: almacen?.permiteMovsSinExistencia ?? true
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const shouldReset = await onSave(values);
      if (shouldReset) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  const reset = () => formik.resetForm({ values: initialValues });

  return (
    <EditForm
      module="almacenes"
      title="Almacenes"
      readonly={!activo}
      returnLink="/almacenes"
      loading={loading}
      showActivate={false}
      showDelete={esEditar}
      onSave={formik.handleSubmit}
      onClear={reset}
      onDelete={onDelete}
      onActivate={onActivate}
    >
      <Grid>
        <Grid container spacing={2}>
          <Grid>
            <AppTextField
              name="id"
              label="Código"
              placeholder="Código"
              maxLength={4}
              uppercase={true}
              value={formik.values.id}
              readOnly={esEditar}
              disabled={loading}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.id && Boolean(formik.errors.id)}
              helperText={getLengthOrErrorText(
                formik.touched.id ? formik.errors.id : '',
                formik.values.id.length,
                4
              )}
            />
          </Grid>
          <Grid size="grow">
            <AppTextField
              name="nombre"
              label="Nombre"
              placeholder="Nombre"
              maxLength={40}
              disabled={loading || inactivo}
              value={formik.values.nombre}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nombre && Boolean(formik.errors.nombre)}
              helperText={getLengthOrErrorText(
                formik.touched.nombre ? formik.errors.nombre : '',
                formik.values.nombre.length,
                40
              )}
            />
          </Grid>
          <Grid>
            <LabeledSwitch
              name="permiteMovsSinExistencia"
              label="Acepta movimientos sin existencia"
              disabled={loading || inactivo}
              checked={formik.values.permiteMovsSinExistencia}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 3 }}>
        <AppTextField label="Status" value={status} disabled={true} />
      </Grid>
    </EditForm>
  );
};

export default AlmacenForm;
